import React from 'react';
import { Dialog, Box, Typography, Button, Stack, useTheme } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SmsIcon from '@mui/icons-material/Sms';
import InsightsIcon from '@mui/icons-material/Insights';
import { motion } from 'framer-motion';
import { usePreferences } from '../../contexts/PreferencesContext';

const MotionBox = motion.create(Box);

const Highlight = ({ icon, label, delay }) => (
    <MotionBox
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.4, ease: 'easeOut' }}
        sx={{
            flex: 1,
            minWidth: 0,
            p: 1.5,
            borderRadius: '14px',
            bgcolor: 'action.hover',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
            textAlign: 'center',
        }}
    >
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{label}</Typography>
    </MotionBox>
);

const WelcomeDialog = ({ open, onStart, onSkip }) => {
    const theme = useTheme();
    const { t } = usePreferences();

    return (
        <Dialog
            open={open}
            onClose={onSkip}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden' } }}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}
        >
            {/* Gradient hlavička s animovanou vlnou */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 55%, #F59E0B 145%)',
                    p: 4,
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <MotionBox
                    initial={{ scale: 0, rotate: -30 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 16, delay: 0.1 }}
                    sx={{
                        width: 84,
                        height: 84,
                        borderRadius: '26px',
                        bgcolor: 'rgba(255,255,255,0.15)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 44,
                        boxShadow: '0 16px 40px -12px rgba(0,0,0,0.4)',
                    }}
                >
                    <motion.span
                        animate={{ rotate: [0, 18, -8, 14, 0] }}
                        transition={{ delay: 0.7, duration: 1.2, ease: 'easeInOut' }}
                        style={{ display: 'inline-block', transformOrigin: '70% 70%' }}
                    >
                        👋
                    </motion.span>
                </MotionBox>
            </Box>

            <Box sx={{ p: 3, pt: 2.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center' }}>
                    {t('onboardingFlow.welcome.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                    {t('onboardingFlow.welcome.subtitle')}
                </Typography>

                <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                    <Highlight delay={0.25} icon={<QrCode2Icon sx={{ color: theme.palette.primary.main }} />} label={t('onboardingFlow.welcome.highlightQr')} />
                    <Highlight delay={0.35} icon={<SmsIcon sx={{ color: '#F59E0B' }} />} label={t('onboardingFlow.welcome.highlightSms')} />
                    <Highlight delay={0.45} icon={<InsightsIcon sx={{ color: '#16a34a' }} />} label={t('onboardingFlow.welcome.highlightInsights')} />
                </Stack>

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={onStart}
                    sx={{ mt: 3, borderRadius: '14px', py: 1.25, fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                >
                    {t('onboardingFlow.welcome.start')}
                </Button>
                <Button fullWidth onClick={onSkip} sx={{ mt: 1, borderRadius: '14px', color: 'text.secondary', textTransform: 'none' }}>
                    {t('onboardingFlow.welcome.skip')}
                </Button>
            </Box>
        </Dialog>
    );
};

export default WelcomeDialog;
